import { CloseCircleOutlined, DeleteFilled } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  ASSIGN_CHANNEL_MEMBER,
  Channel,
  ErrorResponse,
  GET_ALL_USERS_BY_FILTER,
  GET_CHANNEL_MEMBERS,
  UNASSIGN_CHANNEL_MEMBER,
  User,
} from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import { App, Button, List, Select, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation'; // Added translation import
import { useEffect, useState } from 'react';

/* eslint-disable-next-line */
export interface AssignReviewersProps {
  channelId?: string;
  handleSaveChannel?: () => Promise<Channel | null>;
}

const WAIT_INTERVAL = 500;

export function AssignReviewers({
  channelId,
  handleSaveChannel,
}: AssignReviewersProps) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common'); // Added translation hook
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expertId, setExpertId] = useState<string>();

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [
    getReviewers,
    { data: reviewers, loading: reviewersLoading, refetch: refetchReviewers },
  ] = useLazyQuery<{
    getChannelMembers: {
      user: User;
    }[];
  }>(GET_CHANNEL_MEMBERS, {
    fetchPolicy: 'no-cache',
  });

  const [searchUser, { data: userData, loading: userLoading }] = useLazyQuery<{
    publicUsers: User[];
  }>(GET_ALL_USERS_BY_FILTER);

  const [assignMember, { loading: assignLoading }] = useMutation(
    ASSIGN_CHANNEL_MEMBER,
    {
      refetchQueries: [
        {
          query: GET_CHANNEL_MEMBERS,
          variables: { channelId, channelMemberType: 'CHANNEL_REVIEWER' },
        },
      ],
      onError: handleError,
      onCompleted() {
        setExpertId(undefined);
        refetchReviewers();
        notification.success({
          message: t('reviewer_assigned_successfully'),
        });
      },
    }
  );
  const [unassignMember, { loading: unassignLoading }] = useMutation(
    UNASSIGN_CHANNEL_MEMBER,
    {
      onError: handleError,
      refetchQueries: [
        {
          query: GET_CHANNEL_MEMBERS,
          variables: { channelId, channelMemberType: 'CHANNEL_REVIEWER' },
        },
      ],
      onCompleted() {
        refetchReviewers();
        notification.success({
          message: t('successfully_removed'),
        });
      },
    }
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim());
  };

  const handleDeleteClick = (id: number) => {
    unassignMember({
      variables: {
        input: {
          channelId: channelId,
          userId: id,
          channelMemberType: 'CHANNEL_REVIEWER',
        },
      },
    });
  };

  const handleAddClick = async () => {
    if (expertId) {
      let id = channelId;
      if (!channelId) {
        if (handleSaveChannel) {
          const data = await handleSaveChannel();
          if (!data) return;
          id = data?.id;
        }
      }
      await assignMember({
        variables: {
          input: {
            channelId: id,
            userId: expertId,
            memberType: 'CHANNEL_REVIEWER',
          },
        },
      });
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchUser({
        variables: {
          searchBy: searchTerm,
          filterBy: {
            roles: ['ADMIN', 'INTRAPRENEUR', 'CHALLENGE_MANAGER'],
            field: 'email',
          },
          limit: 100,
        },
      });
    }, WAIT_INTERVAL);

    return () => clearTimeout(delayDebounceFn);
  }, [searchUser, searchTerm]);

  useEffect(() => {
    if (channelId) {
      getReviewers({
        variables: {
          channelMemberType: 'CHANNEL_REVIEWER',
          channelId: channelId,
        },
      });
    }
  }, [channelId, getReviewers]);

  return (
    <div className={!channelId ? 'cursor-not-allowed' : ''}>
      <div className={!channelId ? 'opacity-50 pointer-events-none' : ''}>
        <div className="space-y-3">
          <div className="text-gray-700">
            {t('assign_channel_manager_description')}
          </div>
          <div className="flex items-center gap-4">
            <Select
              showSearch
              allowClear
              placeholder={t('search_by_email')}
              value={expertId}
              loading={userLoading}
              className="w-full"
              notFoundContent={
                userLoading ? (
                  <div className="text-center">
                    <Spin size="small" />
                  </div>
                ) : null
              }
              filterOption={(input, option) =>
                ((option?.label as string) ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value: string) => setExpertId(value)}
              onSearch={handleSearch}
            >
              {userData?.publicUsers
                .filter(
                  (user) =>
                    !reviewers?.getChannelMembers.find(
                      (reviewer) => reviewer.user.id === user.id
                    )
                )
                .map((item) => (
                  <Select.Option
                    key={item.id}
                    value={item.id}
                    label={item.email}
                  >
                    <div className="flex items-center gap-2">
                      <UserAvatar size={24} user={item} />
                      {item.email}
                    </div>
                  </Select.Option>
                ))}
            </Select>
            <Button
              shape="round"
              disabled={!expertId}
              loading={assignLoading}
              onClick={handleAddClick}
            >
              {t('add')}
            </Button>
          </div>
          <List
            size="small"
            loading={reviewersLoading}
            dataSource={reviewers?.getChannelMembers}
            split={false}
            renderItem={(item) => (
              <List.Item className="flex items-center gap-2 !p-0 !py-1">
                <div className="flex items-center gap-2">
                  <UserAvatar user={item.user} size={32} />
                  <div className="">
                    <div className="text-gray-800 text-sm font-medium">
                      {item.user.fullName}
                    </div>
                    <div className="text-gray-700 text-sm font-medium">
                      {item.user.email}
                    </div>
                  </div>
                </div>
                <div>
                  <Button
                    shape="circle"
                    disabled={unassignLoading}
                    onClick={() => handleDeleteClick(item.user.id)}
                  >
                    <DeleteFilled />
                  </Button>
                </div>
              </List.Item>
            )}
          />
        </div>
      </div>
      {!channelId && (
        <div className="text-center text-gray-800 mt-4">
          {t('create_channel_first')}
        </div>
      )}
    </div>
  );
}

export default AssignReviewers;
